import { CheckRequestStatus, LocationCheckErrorType } from '~/utils/geocomply';
import { LocationProviderQueryKeys } from '~/components/providers/query';
import { reportEvent } from '~/domains/analytics';

import type { AccessChecksProps, ProtectionGuardType } from '../types';

function geoComplyCheck({
  action,
  contestId,
  geoPermissionStatus,
  isLocationLoading,
  locationErrors,
  locationError,
  locationState,
  locationStatus,
  allowedLocation,
  userId,
  userAge,
  closeModalsByType,
  forceRerender,
  handleOnAccessFailed,
  openModalUnique,
  refetchLocationToken,
}: AccessChecksProps<ProtectionGuardType.GEOCOMPLY>) {
  if (isLocationLoading) return;

  const locationTokenQueryKey = LocationProviderQueryKeys.LOCATION_TOKEN(
    userId,
    geoPermissionStatus
  );

  const reportFailedEvent = () => {
    reportEvent('ProtectionGuard > GeoComply Check Failed', {
      action,
      contestId,
      geoPermissionStatus,
      locationErrors,
      locationState,
      locationStatus,
      userId,
      userAge,
    });
  };

  if (locationError?.type === LocationCheckErrorType.PERMISSION_REQUIRED) {
    openModalUnique({
      type: 'LocationPermissionRequiredModal',
      onRecheckPermission: () => {
        closeModalsByType('LocationPermissionRequiredModal');
        forceRerender();
      },
    }).then(handleOnAccessFailed);

    reportFailedEvent();
    return false;
  }

  if (locationError?.type === LocationCheckErrorType.UNDER_AGE) {
    openModalUnique({
      type: 'AgeRestrictionModal',
      queryKey: locationTokenQueryKey,
    }).then(handleOnAccessFailed);

    reportFailedEvent();
    return false;
  }

  if (
    locationStatus === CheckRequestStatus.DENIED ||
    locationError?.type === LocationCheckErrorType.INVALID_LOCATION
  ) {
    const invalidLocationErrors = locationErrors?.filter(
      (error) => error.type === LocationCheckErrorType.INVALID_LOCATION
    );
    const handleRecheckLocation = async () => {
      closeModalsByType('InvalidLocationModal');
      await refetchLocationToken(true);
      await forceRerender();
    };

    openModalUnique({
      type: 'InvalidLocationModal',
      locationErrors: invalidLocationErrors,
      onRecheckLocation: handleRecheckLocation,
      queryKey: locationTokenQueryKey,
    }).then(handleOnAccessFailed);

    reportFailedEvent();
    return false;
  }
  if (locationStatus === CheckRequestStatus.ALLOWED) {
    closeModalsByType('InvalidLocationModal');
    closeModalsByType('AgeRestrictionModal');
    closeModalsByType('LocationPermissionRequiredModal');
  }

  const verificationErrors = locationErrors?.filter(
    (error) =>
      error.type !== LocationCheckErrorType.INVALID_LOCATION &&
      error.type !== LocationCheckErrorType.UNDER_AGE
  );

  if (verificationErrors?.length) {
    const handleRecheckLocation = async () => {
      closeModalsByType('LocationErrorModal');
      await refetchLocationToken(true);
      await forceRerender();
    };

    openModalUnique({
      type: 'LocationErrorModal',
      locationErrors: verificationErrors,
      onRecheckLocation: handleRecheckLocation,
      queryKey: locationTokenQueryKey,
    }).then(handleOnAccessFailed);

    reportFailedEvent();
    return false;
  }
  if (allowedLocation || locationErrors?.length === 0) {
    closeModalsByType('LocationErrorModal');
  }

  return true;
}

export { geoComplyCheck };
